import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import Hero from '../components/Hero';
import Footer from '../components/Footer';
import { Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [photography, setPhotography] = useState([]);
  const location = useLocation();

  // Handle hash scrolling when navigating from other pages
  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace('#', '');
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'instant' });
      }
    }
  }, [location]);

  const partners = [
    { 
      name: 'Channel 4',
      logo: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/9v8z9exv_Channel-4-Logo.png',
      size: 'normal'
    },
    {
      name: 'HBO',
      logo: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/vtw6k29t_hbo-2.png',
      size: 'normal'
    },
    { 
      name: 'Vice News Tonight',
      logo: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/09qhge33_44532.png',
      size: 'large'
    },
    {
      name: 'Historic Royal Palaces',
      logo: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/8al5wsi8_hrp_1.png',
      size: 'large'
    },
    {
      name: 'Design Museum',
      logo: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/0uqmg91u_image_processing20240522-2513427-md5lvm.webp',
      size: 'normal'
    },
    {
      name: 'Greater London Authority',
      logo: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/lilqsflh_Logo_of_the_Greater_London_Authority_%28monochrome%29.png',
      size: 'small'
    },
    {
      name: 'Shakespeare Globe',
      logo: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/o1xkatcc_000images-3.png',
      size: 'normal'
    },
    {
      name: 'Royal Opera House',
      logo: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/125qxp65_ROH_Mark.png',
      size: 'large'
    },
    {
      name: 'Surfers Against Sewage',
      logo: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/ac8oyumj_SAS-Texture-Tab-Preferred-Mono-White-RGB.png',
      size: 'normal'
    },
    {
      name: 'Together for Palestine',
      logo: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/yvtd3ia8_maxresdefault.png',
      size: 'xxlarge'
    },
  ];

  // Photography projects for the 2x2 grid
  const photographyProjects = [
    {
      id: 1,
      title: 'Waste Workers of Bahia',
      description: 'Inside Brazil\'s waste crisis',
      image: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/vb91ibyx_000000100014-2.jpg',
      link: '/photography/waste-workers-bahia'
    },
    {
      id: 2,
      title: 'Children of the Lea',
      description: 'Stories from the Lea Valley community',
      image: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/0mm61x2s_000029180001-Edit.jpg',
      link: '/photography/children-of-lea'
    },
    {
      id: 3,
      title: 'WOM Collective',
      description: 'London\'s female-led street art scene',
      image: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/v6dzi2k7_WOM%20Collective.jpg',
      link: '/photography/wom-collective'
    },
    {
      id: 4,
      title: 'Portraits',
      description: 'A selection of portraits',
      image: 'https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/s45hxjc0_000080160013-Edit.jpg',
      link: '/photography/portraits'
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const photographyRes = await axios.get(`${BACKEND_URL}/api/photography`);
        setPhotography(photographyRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div data-testid="home-page" className="bg-cream text-dark">
      <Toaster position="top-center" theme="light" />
      
      <Hero />

      {/* Intro Section */}
      <section data-testid="intro-section" className="py-24 md:py-32 bg-dark">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl">
          <p className="text-2xl md:text-3xl lg:text-4xl leading-relaxed text-cream text-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Films and photography for museums, galleries, and arts institutions so they can bring creative work and its impact into public view.
          </p>
        </div>
      </section>

      {/* Films Section - Main Hero Video */}
      <section id="projects" data-testid="projects-section" className="bg-dark">
        {/* Film Production Title */}
        <div className="py-16 md:py-20 px-6 md:px-12 bg-dark">
          <h2 className="text-6xl md:text-8xl uppercase font-bold text-center text-cream" style={{ fontFamily: 'Anton, sans-serif' }}>
            Film Production
          </h2>
        </div>
        
        {/* KP Loop - Full Screen Video */}
        <div className="relative w-full h-screen">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/75qdk33p_KP_Loop.mp4" type="video/mp4" />
          </video>
        </div>
        
        {/* KP Film Info */}
        <div className="py-12 px-6 md:px-12 bg-dark">
          <div className="max-w-7xl mx-auto">
            <h3 className="text-2xl md:text-3xl lg:text-4xl uppercase text-cream mb-4" style={{ fontFamily: 'Anton, sans-serif' }}>
              Dress Codes exhibition intro film for <span className="font-bold">Kensington Palace</span>
            </h3>
            <div className="flex flex-wrap gap-3 mb-4">
              {['Film installation', 'Director', 'Youth Engagement Project', 'Production Partner', 'National Exhibition'].map((tag, idx) => (
                <span key={idx} className="text-sm uppercase tracking-wider text-dark bg-cream px-3 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
            <Link 
              to="/project/dress-codes" 
              className="text-cream underline hover:text-cream/70 transition-colors"
            >
              View the case study →
            </Link>
          </div>
        </div>

        {/* Film Production Statement */}
        <div className="py-16 md:py-24 px-6 md:px-12 bg-dark">
          <div className="max-w-5xl mx-auto">
            <p className="text-2xl md:text-3xl lg:text-4xl leading-relaxed text-cream text-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Documentary films for cultural institutions and broadcasters so they can communicate the complex social and environmental issues that shape our landscapes and communities.
            </p>
          </div>
        </div>

        {/* Two Films Side by Side - DM Loop and Princes Loop */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 bg-dark px-6 md:px-12">
          {/* Left - DM Loop */}
          <div className="relative flex flex-col">
            <div className="aspect-video bg-dark/50">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              >
                <source src="https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/12va236f_DM_LOOP.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="py-6 bg-dark flex-grow">
              <h4 className="text-xl md:text-2xl uppercase text-cream mb-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                Future Observatories for <span className="font-bold">Design Museum</span>
              </h4>
              <div className="flex flex-wrap gap-2 mb-4">
                {['Film installation', 'Environmental', 'Director', 'Production Partner', 'Factual'].map((tag, idx) => (
                  <span key={idx} className="text-xs uppercase tracking-wider text-dark bg-cream px-2 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
              <Link 
                to="/project/future-observatory" 
                className="text-cream underline hover:text-cream/70 transition-colors"
              >
                View the case study →
              </Link>
            </div>
          </div>

          {/* Right - Princes Loop with Embed */}
          <div className="relative flex flex-col">
            <div className="aspect-video bg-dark/50">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              >
                <source src="https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/2zjkpqdo_Princes_LOOP.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="py-6 bg-dark flex-grow">
              <h4 className="text-xl md:text-2xl uppercase text-cream mb-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                Princes in the Tower for <span className="font-bold">Tower of London</span>
              </h4>
              <div className="flex flex-wrap gap-2 mb-4">
                {['History Doc', 'Camera Operator', 'Online Documentary', 'Factual'].map((tag, idx) => (
                  <span key={idx} className="text-xs uppercase tracking-wider text-dark bg-cream px-2 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
              <Link 
                to="/project/princes-in-tower" 
                className="text-cream underline hover:text-cream/70 transition-colors"
              >
                Watch the film →
              </Link>
            </div>
          </div>
        </div>

        {/* Human-led Storytelling Statement */}
        <div className="py-16 md:py-24 px-6 md:px-12 bg-dark">
          <div className="max-w-5xl mx-auto">
            <p className="text-2xl md:text-3xl lg:text-4xl leading-relaxed text-cream text-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Human-led storytelling grounded in collaboration, commitment and trust.
            </p>
          </div>
        </div>

        {/* Two More Films - Our Baby and Moroccan Meteorite */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 bg-dark px-6 md:px-12">
          {/* Left - Our Baby Vimeo Embed */}
          <div className="relative">
            <div className="aspect-video bg-dark">
              <iframe 
                src="https://player.vimeo.com/video/481431291?h=8b2e2d5a5e&title=0&byline=0&portrait=0" 
                frameBorder="0" 
                allow="autoplay; fullscreen; picture-in-picture" 
                className="w-full h-full"
                title="Our Baby: Modern Miracle | Channel 4 docs"
                allowFullScreen
              />
            </div>
            <div className="py-6 bg-dark">
              <h4 className="text-xl md:text-2xl uppercase text-cream mb-3 whitespace-nowrap" style={{ fontFamily: 'Anton, sans-serif' }}>
                Our Baby: Modern Miracle for <span className="font-bold">Channel 4 Docs</span>
              </h4>
              <div className="flex flex-wrap gap-2">
                {['TV Documentary', 'LGBTQ+', 'Camera Operator', 'Social Documentary', 'Factual'].map((tag, idx) => (
                  <span key={idx} className="text-xs uppercase tracking-wider text-dark bg-cream px-2 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Moroccan Meteorite YouTube Embed */}
          <div className="relative">
            <div className="aspect-video bg-dark">
              <iframe 
                src="https://www.youtube.com/embed/WgW2K5EEW1U?si=6xINq7exhHh9tsqI" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                className="w-full h-full"
                title="Moroccan Meteorite Hunters for Vice News Tonight"
                allowFullScreen
              />
            </div>
            <div className="py-6 bg-dark">
              <h4 className="text-xl md:text-2xl uppercase text-cream mb-3 whitespace-nowrap" style={{ fontFamily: 'Anton, sans-serif' }}>
                Moroccan Meteorite Hunters for <span className="font-bold">Vice News Tonight</span>
              </h4>
              <div className="flex flex-wrap gap-2">
                {['News', 'TV News Segment', 'Factual', 'Post-Production'].map((tag, idx) => (
                  <span key={idx} className="text-xs uppercase tracking-wider text-dark bg-cream px-2 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section data-testid="partners-section" className="py-24 md:py-32 bg-green">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <h2 className="text-6xl md:text-8xl uppercase font-bold mb-16 text-center text-cream" style={{ fontFamily: 'Anton, sans-serif' }}>
            Partners Who Trust the Vision
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 items-center mb-12">
            {partners.map((partner, idx) => (
              <div key={idx} data-testid={`partner-${idx}`} className="flex items-center justify-center">
                <img 
                  src={partner.logo} 
                  alt={partner.name}
                  className={`${
                    partner.size === 'xxlarge' ? 'h-40 md:h-48' :
                    partner.size === 'xlarge' ? 'h-32 md:h-40' : 
                    partner.size === 'large' ? 'h-28 md:h-32' : 
                    partner.size === 'small' ? 'h-14 md:h-16' :
                    'h-20 md:h-24'
                  } w-auto object-contain opacity-80 hover:opacity-100 transition-opacity`}
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <button
              onClick={scrollToContact}
              className="rounded-full border-2 border-cream px-8 py-3 text-sm uppercase tracking-widest text-cream hover:bg-cream hover:text-green transition-all duration-300"
            >
              Enquire Now
            </button>
          </div>
        </div>
      </section>

      {/* Workshops Section */}
      <section id="workshops" data-testid="workshops-section" className="bg-green">
        {/* Workshop Title */}
        <div className="py-16 md:py-24 px-6 md:px-12 bg-green">
          <h2 className="text-6xl md:text-8xl uppercase font-bold text-center text-cream" style={{ fontFamily: 'Anton, sans-serif' }}>
            Creative Workshops
          </h2>
        </div>
        
        {/* KP Workshop Loop - Full Screen Video */}
        <div className="relative w-full h-screen">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/zgztd0ms_KP_workshop_loop.mp4" type="video/mp4" />
          </video>
        </div>
        
        {/* Workshop Info */}
        <div className="py-16 md:py-24 px-6 md:px-12 bg-green">
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-2xl md:text-3xl lg:text-4xl leading-relaxed text-cream mb-12" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Creative workshops using film, photography and experimental techniques to help participants learn and connect through expressing their ideas visually.
            </p>
            <Link
              to="/project/dress-codes"
              className="inline-block rounded-full border-2 border-cream px-8 py-3 text-sm uppercase tracking-widest text-cream hover:bg-cream hover:text-green transition-all duration-300"
            >
              Find out more
            </Link>
          </div>
        </div>
      </section>

      {/* Photography Section - Intro */}
      <section id="photography" data-testid="photography-section" className="py-16 md:py-24 bg-dark">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl">
          <h2 className="text-6xl md:text-8xl uppercase font-bold text-center text-cream mb-12" style={{ fontFamily: 'Anton, sans-serif' }}>
            Photography
          </h2>
          <p className="text-2xl md:text-3xl lg:text-4xl leading-relaxed text-cream text-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Social documentary photographic work shaped through long-term engagement and close relationships, offering detailed, enduring perspectives on communities and environments.
          </p>
        </div>
      </section>

      {/* Photography Grid - 2x2 Portrait */}
      <section className="bg-dark pb-16">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-2 gap-4 md:gap-6">
            {photographyProjects.map((project) => (
              <Link to={project.link} key={project.id} className="group relative overflow-hidden block" style={{ aspectRatio: '4/5' }}>
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center p-4">
                  <h3 
                    className="text-2xl md:text-4xl font-bold uppercase text-center text-cream mb-2"
                    style={{ fontFamily: 'Anton, sans-serif' }}
                  >
                    {project.title}
                  </h3>
                  <p className="text-sm md:text-base text-white text-center">
                    {project.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-6 md:px-12 max-w-6xl">
          <h2 className="text-6xl md:text-8xl uppercase font-bold mb-16 text-center text-green" style={{ fontFamily: 'Anton, sans-serif' }}>
            Let's Connect
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
            {/* Left side - Contact */}
            <div>
              <p className="text-lg md:text-xl mb-8 text-dark/80">
                <strong className="text-dark">Got a project?</strong> Or just want to say hello? Send me a message.
              </p>
              <a
                href="mailto:amydyduch@gmail.com"
                data-testid="contact-email-button"
                className="inline-block rounded-full border-2 border-green px-8 py-3 text-sm uppercase tracking-widest bg-green text-cream hover:bg-green/80 transition-all duration-300 hover:scale-105"
              >
                Email Me
              </a>
            </div>
            
            {/* Right side - Social Links */}
            <div className="bg-green rounded-2xl p-8 h-full">
              <p className="text-lg md:text-xl mb-8 text-cream">
                <strong>Follow me on:</strong>
              </p>
              <div className="flex flex-col space-y-4">
                <a
                  href="https://www.instagram.com/dyduch/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link"
                  className="inline-flex items-center gap-3 rounded-full border-2 border-cream px-8 py-3 text-sm uppercase tracking-widest text-cream hover:bg-cream hover:text-green transition-all duration-300 w-fit"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  Instagram
                </a>
                <a
                  href="https://www.linkedin.com/in/amy-dyduch-b6b4b643/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin-link"
                  className="inline-flex items-center gap-3 rounded-full border-2 border-cream px-8 py-3 text-sm uppercase tracking-widest text-cream hover:bg-cream hover:text-green transition-all duration-300 w-fit"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-green">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl">
          <h2 className="text-6xl md:text-8xl uppercase font-bold mb-8 text-cream text-center" style={{ fontFamily: 'Anton, sans-serif' }}>
            Filmmaker for Arts & Culture
          </h2>
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="w-1/2 md:w-1/3">
              <img
                src="https://customer-assets.emergentagent.com/job_amy-documentary/artifacts/7jbxn2tl_1772810094298.jpeg"
                alt="Amy Dyduch"
                className="w-full h-auto object-cover"
                style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.4), 0 10px 20px rgba(0,0,0,0.3)'
                }}
              />
            </div>
            <div className="flex-1">
              <div className="space-y-4 text-base leading-relaxed text-cream mb-8">
                <p>I am a London-based filmmaker and photographer working across documentary, cultural partnerships, commercials and television.</p>
                <p>I'm interested in how environmental and social systems shape landscapes and communities, with a focus on how people remain imaginative and resilient within conditions of change.</p>
                <p>My films and photographic projects have been exhibited at institutions including <strong className="text-cream">Kensington Palace</strong>, <strong className="text-cream">Tower of London</strong>, and the <strong className="text-cream">Design Museum</strong>, and broadcast on <strong className="text-cream">Channel 4</strong> and <strong className="text-cream">HBO</strong>.</p>
                <p>My work has received international recognition, including a <strong className="text-cream">Eurocities Award</strong> (2024) and a shortlist for the <strong className="text-cream">Photography Foundation Award</strong> (2023).</p>
              </div>
              <p className="text-base leading-relaxed text-cream mb-8 italic">
                Thank you for reading, I'd love to hear from you.
              </p>
              <div className="mb-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_8086167a-f51d-4ef8-80e1-c0055870858a/artifacts/xb9t18j2_logo%20amy%20dyduch%20signature_.png"
                  alt="Amy Dyduch Signature"
                  className="h-16 w-auto"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </div>
              <div className="flex justify-start">
                <button
                  onClick={scrollToContact}
                  className="rounded-full border-2 border-cream px-8 py-3 text-sm uppercase tracking-widest text-cream hover:bg-cream hover:text-green transition-all duration-300"
                >
                  Let's Connect
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
